import { useState } from "react";

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Mail,
  MessageCircle,
  MoreHorizontal,
  User,
} from "lucide-react";

import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useSharedRecords,
  useSharedStats,
} from "@/features/imagings/hooks/useSharedRecords";
import type {
  Imaging,
  ImagingTrackingNotification,
  SharedRecord,
} from "@/features/imagings/schemas/imagings";
import { formatPhoneNumber } from "@/shared/utils/format";

import { SharedRecordDetail } from "./SharedRecordDetail";

interface SharedRecordsListProps {
  imaging: Imaging;
}

/**
 * Componente para mostrar la lista de registros compartidos
 */
export function SharedRecordsList({ imaging }: SharedRecordsListProps) {
  const [selectedRecord, setSelectedRecord] = useState<SharedRecord | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { data: sharedStats } = useSharedStats(imaging.uid);

  const {
    data: trackingData,
    isLoading: isLoadingTracking,
    error: errorTracking,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
    currentPage,
  } = useSharedRecords({
    imaging,
  });

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "viewed":
        return "default";
      case "active":
        return "secondary";
      case "expired":
        return "destructive";
      case "failed":
        return "destructive";
      case "bounce":
        return "destructive";
      case "open":
        return "outline";
      case "sended_api":
        return "outline";
      default:
        return "outline";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "viewed":
        return "Visto";
      case "active":
        return "Activo";
      case "expired":
        return "Expirado";
      case "failed":
        return "Falló";
      case "bounce":
        return "Bounce";
      case "open":
        return "Abierto";
      case "sended_api":
        return "Enviado";
      default:
        return "Desconocido";
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "ws":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const renderTrackingRecord = (tracking: ImagingTrackingNotification) => {
    return (
      <div
        key={`${tracking.id_tracking}-${tracking.to}`}
        className="flex flex-1 items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border"
      >
        <div className="flex items-center gap-3">
          <Badge
            variant="outline"
            className={`text-xs ${tracking.object_shared === "imaging_access" ? "text-blue-600" : "text-green-600"} ${tracking.object_shared === "imaging_access" ? "bg-blue-50 dark:bg-blue-900/20" : "bg-green-50 dark:bg-green-900/20"}`}
          >
            {tracking.object_shared === "imaging_access" ? (
              <Eye className="h-5 w-5 text-blue-600" />
            ) : (
              <FileText className="h-5 w-5 text-green-600" />
            )}
            {tracking.object_shared === "imaging_access" ? "Estudio" : "Informe"}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {getTypeIcon(tracking.type)}
          <span className="font-medium text-sm truncate">
            {tracking.to.includes("@") ? tracking.to : formatPhoneNumber(tracking.to)}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant={getStatusBadgeVariant(tracking.status)}>
            {getStatusText(tracking.status)}
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  // Convertir tracking a SharedRecord para el modal de detalles
                  const record: SharedRecord = {
                    id: tracking.id_tracking,
                    imaging: tracking.imaging,
                    user_receiving: tracking.to,
                    user_sharing: tracking.sender,
                    date_shared: new Date(tracking.created_at).toISOString(),
                    time_expired: "", // No disponible en tracking
                    created_at: new Date(tracking.created_at).toISOString(),
                    phone: tracking.to,
                    status:
                      tracking.status === "delivered"
                        ? "viewed"
                        : tracking.status === "failed"
                          ? "expired"
                          : "active",
                    object_shared: tracking.object_shared,
                    type: tracking.type,
                    manual: tracking.manual,
                  };
                  setSelectedRecord(record);
                  setIsDetailOpen(true);
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                Ver detalles
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const renderPagination = (totalCount: number, type: string) => {
    if (!totalCount || totalCount === 0) return null;

    const totalPages = Math.ceil(totalCount / 5);

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {Math.min((currentPage + 1) * 5, totalCount)} de {totalCount}{" "}
          {type === "imaging" ? "estudios" : "informes"} compartidos
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          <span className="text-sm font-medium">
            Página {currentPage + 1} de {totalPages}
          </span>

          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Registros Compartidos
          </CardTitle>
          <CardDescription>
            Lista de personas a las que se han compartido estudios e informes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sharedStats && (
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {sharedStats.trackingNotifications}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Registros de Tracking
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-4">
              {isLoadingTracking ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : errorTracking ? (
                <div className="text-center py-8 text-red-500">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-300" />
                  <p>Error al cargar registros de tracking</p>
                  <p className="text-sm text-gray-500 mt-2">{errorTracking.message}</p>
                </div>
              ) : trackingData && trackingData.trackingNotifications.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {trackingData.trackingNotifications.map((tracking) =>
                      renderTrackingRecord(tracking)
                    )}
                  </div>
                  {renderPagination(trackingData.totalCount, "imaging")}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay registros de tracking</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <SharedRecordDetail
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />
    </>
  );
}
